import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Gauge, Users, BedDouble, Ruler, ArrowUpRight } from "lucide-react";
import { useI18n } from "../context/I18nContext";
import { saveListScroll } from "../lib/scrollMemory";

function specChips(item, specLabel) {
  const s = item.specs || {};
  const low = (k) => specLabel(k).toLowerCase();
  if (item.category === "cars")
    return [
      { icon: Gauge, text: s.power },
      { icon: Users, text: s.seats ? `${s.seats} ${low("seats")}` : null },
      { icon: null, text: s.transmission },
    ];
  if (item.category === "yachts")
    return [
      { icon: Ruler, text: s.length },
      { icon: BedDouble, text: s.cabins ? `${s.cabins} ${low("cabins")}` : null },
      { icon: Users, text: s.guests ? `${s.guests} ${low("guests")}` : null },
    ];
  return [
    { icon: BedDouble, text: s.bedrooms ? `${s.bedrooms} ${low("bedrooms")}` : null },
    { icon: Users, text: s.travelers ? `${s.travelers} ${low("travelers")}` : null },
    { icon: Ruler, text: s.surface },
  ];
}

export const ListingCard = ({ item, index = 0 }) => {
  const { t, money, specLabel } = useI18n();
  const chips = specChips(item, specLabel).filter((c) => c.text);
  const unavailable = item.available === false;

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6, delay: (index % 3) * 0.08, ease: [0.16, 1, 0.3, 1] }}
    >
      <Link
        to={`/annonce/${item.id}`}
        data-testid={`listing-card-${item.id}`}
        onClick={() => saveListScroll(item.id)}
        className="group block rounded-[24px] overflow-hidden bg-[#0C0C0F] border border-[#D4AF37]/15 shadow-[0_8px_30px_rgba(0,0,0,0.5)] hover:border-[#D4AF37]/45 hover:shadow-[0_22px_60px_rgba(212,175,55,0.14)] hover:-translate-y-1.5 transition-all duration-500"
      >
        <div className="relative aspect-[4/3] overflow-hidden m-2 rounded-[18px]">
          <img
            src={item.images?.[0]}
            alt={item.title}
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[900ms] ease-out"
          />
          <span className="absolute top-3 left-3 text-[10px] tracking-luxe uppercase bg-black/70 backdrop-blur-md px-3 py-1.5 rounded-full text-[#E5C378] border border-[#D4AF37]/30">
            {t(`badge.${item.category}`)}
          </span>
          {unavailable && (
            <span className="absolute top-3 right-3 text-[10px] tracking-widest uppercase bg-[#FF3B30]/90 px-3 py-1.5 rounded-full text-white">
              {t("common.unavailable")}
            </span>
          )}
          {item.available === true && (
            <span className="absolute top-3 right-3 text-[10px] tracking-widest uppercase bg-[#30D158]/90 px-3 py-1.5 rounded-full text-white">
              {t("common.available")}
            </span>
          )}
          <div className="absolute bottom-3 right-3 w-11 h-11 rounded-full bg-[#D4AF37] text-black flex items-center justify-center shadow-lg opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-500">
            <ArrowUpRight className="w-5 h-5" />
          </div>
        </div>

        <div className="px-5 pb-5 pt-2">
          <div className="flex items-center gap-1.5 text-[#7E8C84] text-xs mb-2">
            <MapPin className="w-3.5 h-3.5 text-[#D4AF37]" />
            {item.location}
          </div>
          <h3 className="font-display text-2xl text-[#F5F0E1] leading-tight group-hover:text-[#E5C378] transition-colors">
            {item.title}
          </h3>

          <div className="flex flex-wrap gap-3 mt-4 mb-5">
            {chips.map((c, i) => (
              <span key={i} className="font-mono2 text-[11px] text-[#93A099] flex items-center gap-1.5">
                {c.icon && <c.icon className="w-3.5 h-3.5 text-[#D4AF37]/80" />}
                {c.text}
              </span>
            ))}
          </div>

          <div className="flex items-end justify-between pt-4 border-t border-[#D4AF37]/12">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#5E6B64]">{t("common.from")}</div>
              <div className="font-display text-xl font-bold text-[#F5F0E1]">
                {money(item.customerPrice, item.priceUnit)}
              </div>
            </div>
            <span className="text-[11px] tracking-widest uppercase text-[#D4AF37]">
              {t("common.discover")}
            </span>
          </div>
        </div>
      </Link>
    </motion.div>
  );
};
